import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import SiteSchema from "@/components/site/SiteSchema";
import AnalyticsProvider from "@/components/site/AnalyticsProvider";

import HomePage from "@/pages/HomePage";
import CorporatePage from "@/pages/CorporatePage";
import AboutPage from "@/pages/AboutPage";
import FAQPage from "@/pages/FAQPage";
import SamplesPage from "@/pages/SamplesPage";
import CatalogPage from "@/pages/CatalogPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AuthCallback from "@/pages/AuthCallback";
import { api } from "@/lib/api";
import { applyCatalogImageOverrides } from "@/lib/products";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();

  // Defensive: reset scroll to the top of the viewport whenever the route
  // pathname changes (e.g. navigating from /samples/... to /catalog).
  // We intentionally skip hash changes — anchor navigation inside a page still
  // works via native browser behaviour.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    // Intentionally scoped to pathname only — hash-only navigation (e.g. anchor
    // links, OAuth #session_id=...) must not trigger a scroll reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Capture ?ref=CODE into localStorage on any page load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("soc_ref", ref.trim().toUpperCase());
  }, [location.search]);

  // If Emergent OAuth just redirected back with #session_id=..., handle it first (must be synchronous)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/corporate" element={<CorporatePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/catalog" element={<CatalogPage />} />
      <Route path="/samples/:type/:design/:weight" element={<SamplesPage />} />
      <Route path="/admin" element={<AdminGate />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AdminGate() {
  const [state, setState] = useState({ loading: true, user: null, isAdmin: false });

  useEffect(() => {
    // If we just returned from OAuth, AuthCallback will handle it — don't race here
    if (window.location.hash?.includes("session_id=")) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (!cancelled) setState({ loading: false, user: res.data.user, isAdmin: res.data.is_admin });
      } catch {
        if (!cancelled) setState({ loading: false, user: null, isAdmin: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-[#d4af37] font-display text-2xl tracking-widest-2 uppercase animate-pulse">Loading</div>
      </div>
    );
  }
  if (!state.user || !state.isAdmin) return <AdminLogin denied={state.user && !state.isAdmin} />;
  return <AdminDashboard user={state.user} />;
}

export default function App() {
  // Fetch admin-managed catalog image overrides once on load and merge them into the
  // shared product data before/alongside first paint — one silent re-render, no layout shift.
  const [, setImagesSynced] = useState(false);
  useEffect(() => {
    api
      .get("/catalog-images")
      .then((res) => applyCatalogImageOverrides(res.data))
      .catch(() => {})
      .finally(() => setImagesSynced(true));
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <SiteSchema />
        <AnalyticsProvider>
          <AppRouter />
        </AnalyticsProvider>
        <Toaster
          position="bottom-center"
          theme="dark"
          toastOptions={{
            classNames: {
              toast: "glass-strong text-white border-white/10",
              title: "text-white",
              description: "text-white/70",
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}
